import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Onboarding from './pages/Onboarding';
import SignUp from './pages/SignUp';
import SignIn from './pages/SignIn';
import ProfileSetup from './pages/ProfileSetup';
import Impersonate from './pages/Impersonate';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import HomeFeed from './pages/HomeFeed';
import CreatorProfile from './pages/CreatorProfile';
import TradingSheet from './pages/TradingSheet';
import Wallet from './pages/Wallet';
import AccountSettings from './pages/AccountSettings';
import ProfilePage from './pages/ProfilePage';
import ReferralsPage from './pages/ReferralsPage';
import PreferencesPage from './pages/PreferencesPage';
import CreateClan from './pages/CreateClan';
import ManageClan from './pages/ManageClan';
import ClanFeed from './pages/ClanFeed';
import StubCheckout from './pages/StubCheckout';
import Notifications from './pages/Notifications';
import MyClans from './pages/MyClans';
import Launches from './pages/Launches';
import Trade from './pages/Trade';
import LaunchDetail from './pages/LaunchDetail';
import ScheduleLaunch from './pages/ScheduleLaunch';
import Pulse from './pages/Pulse';
import Bounties from './pages/Bounties';
import Perks from './pages/Perks';
import HelpCenter from './pages/HelpCenter';
import AdminOverview from './pages/AdminOverview';
import AdminUsers from './pages/AdminUsers';
import AdminTransactions from './pages/AdminTransactions';
import AdminWithdrawals from './pages/AdminWithdrawals';
import AdminDeposits from './pages/AdminDeposits';
import AdminVerifications from './pages/AdminVerifications';
import AdminSettings from './pages/AdminSettings';
import AdminCategories from './pages/AdminCategories';
import AdminWaitlist from './pages/AdminWaitlist';
import AdminCreatorWaitlist from './pages/AdminCreatorWaitlist';
import LocaleNudge from './components/LocaleNudge';
import AdminReferrals from './pages/AdminReferrals';
import AdminSeed from './pages/AdminSeed';
import AdminDemo from './pages/AdminDemo';
import AdminLaunches from './pages/AdminLaunches';
import GuideCreatorPrep from './pages/GuideCreatorPrep';
import GuideAudiencePrep from './pages/GuideAudiencePrep';
import OnboardingTour from './components/OnboardingTour';
import WelcomeModal from './components/WelcomeModal';
import CreatorTour from './components/CreatorTour';
import Landing from './pages/Landing';
import Verify from './pages/Verify';
import Bookmarks from './pages/Bookmarks';
import Waitlist from './pages/Waitlist';
import AdminPointsLog from './pages/AdminPointsLog';
import FeaturedPurchase from './pages/FeaturedPurchase';
import Legal from './pages/Legal';
import Leaderboards from './pages/Leaderboards';
import Messages from './pages/Messages';
import MaintenanceGate from './components/MaintenanceGate';

function ProtectedRoute({ children, requireCompleteProfile = true }) {
  const { user } = useAuth();
  if (user === null) return <div className="loading-shell">loading…</div>;
  if (user === false) return <Navigate to="/onboarding" replace />;
  if (requireCompleteProfile && !user.profile_complete) {
    return <Navigate to="/profile-setup" replace />;
  }
  return children;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="loading-shell">loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/home" replace />;
  return children;
}

function AnonOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="loading-shell">loading…</div>;
  if (user && typeof user === 'object') {
    return <Navigate to={user.profile_complete ? '/home' : '/profile-setup'} replace />;
  }
  return children;
}

/** Renders <Landing/> for anon visitors; sends authed users to /home. */
function AuthedRedirectHome({ fallback }) {
  const { user } = useAuth();
  if (user === null) return <div className="loading-shell">loading…</div>;
  if (user && typeof user === 'object') {
    return <Navigate to={user.profile_complete ? '/home' : '/profile-setup'} replace />;
  }
  return fallback;
}

function Router() {
  return (
    <Routes>
      {/* Authenticated users hit /home; anon visitors see the landing page. */}
      <Route path="/" element={<AuthedRedirectHome fallback={<Landing />} />} />
      <Route path="/onboarding" element={<AnonOnly><Onboarding /></AnonOnly>} />
      <Route path="/signup" element={<AnonOnly><SignUp /></AnonOnly>} />
      <Route path="/login" element={<AnonOnly><SignIn /></AnonOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/guides/creator-prep" element={<GuideCreatorPrep />} />
      <Route path="/guides/audience-prep" element={<GuideAudiencePrep />} />
      <Route
        path="/profile-setup"
        element={
          <ProtectedRoute requireCompleteProfile={false}>
            <ProfileSetup />
          </ProtectedRoute>
        }
      />
      <Route path="/impersonate" element={<Impersonate />} />
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <HomeFeed />
          </ProtectedRoute>
        }
      />
      <Route
        path="/creator/:handle"
        element={
          <ProtectedRoute>
            <CreatorProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trade/:handle"
        element={
          <ProtectedRoute>
            <TradingSheet />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wallet"
        element={
          <ProtectedRoute>
            <Wallet />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AccountSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/referrals"
        element={
          <ProtectedRoute>
            <ReferralsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/preferences"
        element={
          <ProtectedRoute>
            <PreferencesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-clan"
        element={
          <ProtectedRoute>
            <CreateClan />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-clan"
        element={
          <ProtectedRoute>
            <ManageClan />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clan/:handle/feed"
        element={
          <ProtectedRoute>
            <ClanFeed />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sandbox-checkout/:id"
        element={
          <ProtectedRoute>
            <StubCheckout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-clans"
        element={
          <ProtectedRoute>
            <MyClans />
          </ProtectedRoute>
        }
      />
      <Route
        path="/launches"
        element={
          <ProtectedRoute>
            <Launches />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trade"
        element={
          <ProtectedRoute>
            <Trade />
          </ProtectedRoute>
        }
      />
      <Route
        path="/launch/:id"
        element={
          <ProtectedRoute>
            <LaunchDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedule-launch"
        element={
          <ProtectedRoute>
            <ScheduleLaunch />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pulse"
        element={
          <ProtectedRoute>
            <Pulse />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bounties"
        element={
          <ProtectedRoute>
            <Bounties />
          </ProtectedRoute>
        }
      />
      <Route
        path="/perks"
        element={
          <ProtectedRoute>
            <Perks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <HelpCenter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminOverview />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <AdminRoute>
            <AdminUsers />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/transactions"
        element={
          <AdminRoute>
            <AdminTransactions />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/deposits"
        element={
          <AdminRoute>
            <AdminDeposits />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/withdrawals"
        element={
          <AdminRoute>
            <AdminWithdrawals />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/verifications"
        element={
          <AdminRoute>
            <AdminVerifications />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <AdminRoute>
            <AdminSettings />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/categories"
        element={
          <AdminRoute>
            <AdminCategories />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/waitlist"
        element={
          <AdminRoute>
            <AdminWaitlist />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/creator-waitlist"
        element={
          <AdminRoute>
            <AdminCreatorWaitlist />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/referrals"
        element={
          <AdminRoute>
            <AdminReferrals />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/seed"
        element={
          <AdminRoute>
            <AdminSeed />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/demo"
        element={
          <AdminRoute>
            <AdminDemo />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/launches"
        element={
          <AdminRoute>
            <AdminLaunches />
          </AdminRoute>
        }
      />
      <Route
        path="/verify"
        element={
          <ProtectedRoute>
            <Verify />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bookmarks"
        element={
          <ProtectedRoute>
            <Bookmarks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/waitlist"
        element={
          <ProtectedRoute>
            <Waitlist />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/points-log"
        element={
          <ProtectedRoute>
            <AdminPointsLog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/featured/buy"
        element={
          <ProtectedRoute>
            <FeaturedPurchase />
          </ProtectedRoute>
        }
      />
      <Route
        path="/legal"
        element={
          <ProtectedRoute>
            <Legal />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leaderboards"
        element={
          <ProtectedRoute>
            <Leaderboards />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages/:handle"
        element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/onboarding" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <MaintenanceGate>
        <LocaleNudge />
        <Router />
        <OnboardingTour />
        <WelcomeModal />
        <CreatorTour />
      </MaintenanceGate>
    </AuthProvider>
  );
}
